import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty, NeedClinic } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, fmtDate, useLoad } from "@/lib/api";
import { can, EXPENSE_CATEGORIES } from "@/lib/constants";

const today = () => new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const [rows, reload] = useLoad(activeClinicId ? "/expenses" : null, { clinic_id: activeClinicId });
  const [f, setF] = useState(null);
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const cur = activeClinic.currency;
  const total = (rows || []).reduce((a, e) => a + e.amount, 0);
  const save = async () => {
    try { await api.post("/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense recorded"); setF(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Expenses" subtitle={`Total recorded: ${money(total, cur)}`}>
        {can(user, "addExpense") && <Button className="rounded-full" onClick={() => setF({ category: "medical_supplies", amount: "", date: today(), vendor: "", description: "" })} data-testid="add-expense-button"><Plus className="mr-2 h-4 w-4" />Record expense</Button>}
      </PageHeader>
      <Panel testId="expenses-table">
        {!rows?.length ? <Empty text="No expenses recorded." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Vendor</TableHead><TableHead>Description</TableHead><TableHead>By</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-sm text-slate-500">{fmtDate(e.date)}</TableCell>
                <TableCell>{EXPENSE_CATEGORIES[e.category]}</TableCell>
                <TableCell>{e.vendor || "—"}</TableCell>
                <TableCell className="max-w-[240px] truncate">{e.description || "—"}</TableCell>
                <TableCell className="text-sm">{e.recorded_by_name}</TableCell>
                <TableCell className="text-right font-semibold text-rose-600">{money(e.amount, cur)}</TableCell>
              </TableRow>))}</TableBody>
          </Table></div>)}
      </Panel>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record expense</DialogTitle></DialogHeader>
          {f && <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(EXPENSE_CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></Field>
            <Field label={`Amount (${cur})`}><Input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" /></Field>
            <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="expense-date-input" /></Field>
            <Field label="Vendor"><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="expense-vendor-input" /></Field>
            <Field label="Description" className="col-span-2"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="expense-description-input" /></Field>
          </div>}
          <DialogFooter><Button onClick={save} data-testid="expense-form-submit-button">Save expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
